import type {
  ButtonNavItem,
  LanguageNavItem,
  PageNavItem,
  ThemeNavItem,
  UrlNavItem,
} from '@unifyapps/defs/types/interface';
import { getIconFromRegistry } from '@unifyapps/icons/utils/getIconFromRegistry';
import { clsx } from 'clsx';
import { useCallback, useMemo } from 'react';
import { getIsPathActive } from '../components/NavigationContainer/variants/SideNavigation/helpers';
import { INTERFACE_LOCALES } from '../no-code/const/locales';
import { useReactiveComputeContext } from '../no-code/hooks/computeContext';
import { useGetNoCodeComputedData } from '../no-code/hooks/computeContext/useGetNoCodeComputedData';
import { useComputeConditionEvaluationGetter } from '../no-code/hooks/useComputeConditionEvaluationGetter';
import { useGlobalAppStateStore } from '../no-code/stores/app/GlobalAppStateStore';
import InterfaceHelper from '../no-code/stores/InterfaceStore/helper/InterfaceHelper';
import { getConditionalPropertyValue } from '../utils/conditionalValues';
import { isNavSectionVisible } from '../utils/getIsNavSectionVisible';

type UseUserMenuNavItemsProps = {
  userMenuNavItems?: (UrlNavItem | ThemeNavItem | LanguageNavItem | ButtonNavItem | PageNavItem)[];
  activePathnames?: string[];
};

export function useUserMenuNavItems({
  userMenuNavItems,
  activePathnames,
}: UseUserMenuNavItemsProps) {
  const { context } = useReactiveComputeContext();
  const getComputedFilters = useComputeConditionEvaluationGetter();
  const { getNoCodeComputedData } = useGetNoCodeComputedData();

  const getNavIcon = useCallback(
    (icon: UrlNavItem['icon']) => {
      if (typeof icon === 'string') return getIconFromRegistry(icon);

      const iconName = getConditionalPropertyValue({
        value: getComputedFilters(icon, context) as string,
      });
      return typeof iconName === 'string' ? getIconFromRegistry(iconName) : undefined;
    },
    [context, getComputedFilters],
  );

  const userMenuNavMenuItems = useMemo(
    () =>
      userMenuNavItems?.map((item) => {
        const NavIcon =
          item.type !== 'LANGUAGE_SWITCHER' && item.type !== 'COLOR_SCHEME_SWITCHER'
            ? getNavIcon(item.icon)
            : undefined;

        const isSelected =
          item.type === 'URL' && activePathnames
            ? !!item.url && getIsPathActive({ activePathnames, path: item.url })
            : false;

        const evaluatedName = getNoCodeComputedData
          ? (getNoCodeComputedData(item.name, context) as string)
          : item.name;

        const events = item.type === 'BUTTON' ? item.interactions?.events : [];

        return {
          type:
            item.type === 'URL'
              ? ('label' as const)
              : (item.type as 'label' | 'BUTTON' | 'COLOR_SCHEME_SWITCHER' | 'LANGUAGE_SWITCHER'),
          id: item.id,
          title: evaluatedName ?? item.name,
          url: item.type === 'URL' && item.url,
          selected: isSelected,
          className: clsx('py-md ps-sm pe-sm mx-lg my-sm hover:!bg-secondary_hover', {
            '!bg-secondary_hover': isSelected,
            '!bg-transparent': !isSelected,
          }),
          startDecoratorNode: NavIcon ? (
            <NavIcon className="text-icon-primary" height={20} width={20} />
          ) : null,
          events,
          titleClassName: 'font-medium text-secondary',
        };
      }),
    [userMenuNavItems, activePathnames, getNavIcon, context, getNoCodeComputedData],
  );

  const interfaceRecord = useGlobalAppStateStore().hooks.record.useDetails();
  const allowedLocales = InterfaceHelper.getAllLocalesSet(interfaceRecord);

  const localeOptions = useMemo(
    () =>
      Array.from(allowedLocales).map((value) => ({
        value,
        label: INTERFACE_LOCALES.find((locale) => locale.const === value)?.title ?? value,
      })),
    [allowedLocales],
  );

  return {
    userMenuNavMenuItems,
    localeOptions,
    hasNavSection: isNavSectionVisible({ userMenuNavItems, localeOptions }),
  };
}
